import React from "react";
import ActivationComments from "./ActivationComments";

const T = {
  ink:        "#1a1a1c",
  ink2:       "#4a4a50",
  ink3:       "#86868b",
  ink4:       "#b4b3b0",
  hair:       "rgba(20,20,22,0.07)",
  hairStrong: "rgba(20,20,22,0.12)",
  surface:    "rgba(255,255,255,0.80)",
  surfaceSunk:"rgba(20,20,22,0.033)",
  accent:     "#1e3a8a",
  accentSoft: "rgba(30,58,138,0.09)",
  ok:         "#1E8E3E",
  okSoft:     "rgba(30,142,62,0.10)",
  warn:       "#B25E09",
  warnSoft:   "rgba(178,94,9,0.10)",
  amber:      "#d97706",
  danger:     "#C5221F",
  dangerSoft: "rgba(197,34,31,0.10)",
  font:       "'Geist',-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
};

// Minimal icon set (Lucide paths)
function Icon({ name, size = 13 }) {
  const paths = {
    users:     ["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2","M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z","M23 21v-2a4 4 0 0 0-3-3.87","M16 3.13a4 4 0 0 1 0 7.75"],
    megaphone: ["M3 11l19-5v12L3 14v-3z","M11.6 16.8a3 3 0 1 1-5.8-1.6"],
    zap:       ["M13 2L4 14h7l-1 8 9-12h-7z"],
    clock:     ["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z","M12 6v6l4 2"],
    edit:      ["M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7","M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"],
    plus:      ["M12 5v14M5 12h14"],
    check:     ["M20 6L9 17l-5-5"],
    message:   ["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"],
  };
  const d = paths[name] || paths.zap;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {d.map((p, i) => <path key={i} d={p} />)}
    </svg>
  );
}

// Provider brand tags
const PROV_MAP = {
  "Claro":        { c: "#E50914", l: "CL" },
  "Ligga Telecom":{ c: "#7B2CBF", l: "LG" },
  "Vivo":         { c: "#660099", l: "VV" },
  "Ired Telecom": { c: "#1a3a6b", l: "IR" },
};
function provMark(name) {
  return PROV_MAP[name] || { c: "#7B7B7B", l: (name || "??").slice(0, 2).toUpperCase() };
}

const AVATAR_COLORS = [
  { bg: "linear-gradient(135deg,#cfe1ff,#94b8f0)", color: "#1a3a72" },
  { bg: "linear-gradient(135deg,#e3d1fb,#ad8be5)", color: "#3d2070" },
  { bg: "linear-gradient(135deg,#ffd9c2,#f0a87a)", color: "#5f2a0d" },
  { bg: "linear-gradient(135deg,#c5edd9,#7fc8a3)", color: "#1a5238" },
];
function avatarColor(name = "") {
  const n = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}
function initials(name = "") {
  return (name || "").split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase() || "?";
}

// Field
function Field({ label, value, mono, accent, span3, wide }) {
  const isEmpty = !value || value === "—";
  return (
    <div style={{
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 4,
      gridColumn: span3 ? "1 / -1" : wide ? "span 2" : undefined,
    }}>
      <span style={{ fontSize: 10.5, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500, fontFamily: T.font }}>
        {label}
      </span>
      <span style={{
        fontSize: 13.5,
        fontWeight: accent ? 600 : 500,
        color: isEmpty ? T.ink4 : (accent ? T.accent : T.ink),
        fontVariantNumeric: mono ? "tabular-nums" : "normal",
        wordBreak: "break-word",
        fontFamily: T.font,
      }}>
        {value || "—"}
      </span>
    </div>
  );
}

// Section
function Section({ icon, title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{
          width: 24, height: 24, borderRadius: 7,
          background: T.accentSoft, color: T.accent,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <Icon name={icon} size={12} />
        </div>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.ink, fontFamily: T.font }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

const defaultFmt = (v) => {
  if (!v) return "—";
  return new Date(v).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

// Format YYYY-MM-DD → dd/mm/aaaa (avoids UTC offset shifting the day)
function fmtDateField(v) {
  if (!v) return null;
  const s = String(v).trim();
  if (!s) return null;
  // ISO date "YYYY-MM-DD" — add noon to avoid TZ day-shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T12:00:00`);
    return isNaN(d.getTime()) ? s : d.toLocaleDateString("pt-BR");
  }
  // Already pt-BR "DD/MM/YYYY" or other — return as-is
  return s;
}

export default function ActivationDetails({
  data,
  fmtDateTime = defaultFmt,
  clientNameFallback = "—",
  documentFallback = "—",
  phoneFallback = "—",
  mobileFallback = "—",
  onCommentsUpdated,
}) {
  if (!data) return null;

  const name       = data.clientName || data.personName || clientNameFallback || "—";
  const document   = data.document || documentFallback || data.maskedDocument || "—";
  const phone      = data.phone  || phoneFallback  || "—";
  const mobile     = data.mobile || mobileFallback || "—";
  const createdBy  = data.createdByName  || data.createdByEmail  || data.createdBy  || "—";
  const updatedBy  = data.updatedByName  || data.updatedByEmail  || data.updatedBy  || createdBy;
  const activatedBy = data.activatedByName || data.activatedByEmail || "—";

  const av   = avatarColor(name);
  const prov = provMark(data.provider);

  // Value is stored as "R$ 79,00" — use as-is; only skip if empty/placeholder
  const rawVal = String(data.value || "").trim();
  const valueStr = rawVal && rawVal !== "R$" && rawVal !== "R$ " ? rawVal : null;

  // Dates stored as "YYYY-MM-DD" from <input type="date">
  const activationDateFmt   = fmtDateField(data.activationDate);
  const installationDateFmt = fmtDateField(data.installationDate);

  // Timeline events (most recent first)
  const isNegativa = (data.status || "").toUpperCase() === "NEGATIVA";
  // Pure negative record (opened from the Negativações tab)
  const isNegativeRecord = !!data._isNegativeDoc;

  const events = [
    // Pagamento (ativação ATIVADA marcada como paga)
    !isNegativeRecord && data.paidAt && {
      title: "Pagamento registrado",
      who: data.paidByEmail || "—",
      when: fmtDateTime(data.paidAt),
      color: "#0F766E",
    },
    // For an activation that became negativa — show negativa event at disabledAt
    isNegativa && !isNegativeRecord && {
      title: `Negativa registrada${data.negativeReason ? ` — ${data.negativeReason}` : ""}`,
      who: updatedBy,
      when: fmtDateTime(data.disabledAt || data.updatedAt),
      color: T.danger,
    },
    // Activation confirmed (only for activation records)
    !isNegativeRecord && data.activatedAt && {
      title: "Ativação confirmada",
      who: activatedBy,
      when: fmtDateTime(data.activatedAt),
      color: T.ok,
    },
    // Last update (only for non-negativa activations to avoid noise)
    !isNegativa && data.updatedAt && data.updatedAt !== data.createdAt && {
      title: "Última alteração",
      who: updatedBy,
      when: fmtDateTime(data.updatedAt),
      color: T.amber,
    },
    // Creation / negativa registration
    {
      title: isNegativeRecord
        ? `Negativa registrada${data.reason ? ` — ${data.reason}` : ""}`
        : "Ativação criada",
      who: createdBy,
      when: fmtDateTime(data.createdAt),
      color: isNegativeRecord ? T.danger : T.accent,
    },
  ].filter(Boolean);

  return (
    <div style={{ fontFamily: T.font }}>

      {/* ── Hero strip ─────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", gap: 16, alignItems: "center",
        padding: 16,
        background: T.surfaceSunk,
        border: `0.5px solid ${T.hair}`,
        borderRadius: 14,
        marginBottom: 20,
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: av.bg, color: av.color,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 600, fontSize: 16, flexShrink: 0,
          fontFamily: T.font,
        }}>
          {initials(name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em", color: T.ink }}>{name}</div>
          <div style={{ fontSize: 12, color: T.ink2, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
            {document !== "—" && `CPF · ${document}`}
            {document !== "—" && (data.condo || data.unit) && (
              <span style={{ margin: "0 6px", color: T.ink4 }}>·</span>
            )}
            {data.condo || ""}
            {data.unit ? ` / ${data.unit}` : ""}
          </div>
        </div>
        {valueStr && (
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 10.5, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>
              Valor mensal
            </div>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", color: T.accent, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>
              {valueStr}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>

        {/* ── Dados do Cliente ───────────────────────────────────────────── */}
        <Section icon="users" title="Dados do Cliente">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, paddingLeft: 32 }}>
            <Field label="Documento" value={document} mono />
            <Field label="Telefone"  value={phone}    mono />
            <Field label="Celular"   value={mobile}   mono />
            <Field label="Condomínio" value={data.condo} />
            <Field label="Unidade"   value={data.unit} mono />
            <div />
            <Field label="Endereço completo" value={data.address} span3 />
          </div>
        </Section>

        <div style={{ height: 0.5, background: T.hairStrong }} />

        {/* ── Captação ───────────────────────────────────────────────────── */}
        <Section icon="megaphone" title="Forma de Captação">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16, paddingLeft: 32 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 10.5, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>
                Captado por
              </span>
              {data.capture && data.capture !== "—" ? (
                <span style={{
                  display: "inline-flex", alignItems: "center",
                  padding: "3px 10px", fontSize: 12, fontWeight: 500, borderRadius: 999,
                  background: T.accentSoft, color: T.accent, width: "fit-content",
                }}>
                  {data.capture}
                </span>
              ) : (
                <span style={{ fontSize: 13.5, fontWeight: 500, color: T.ink4 }}>—</span>
              )}
            </div>
            <Field label="Notas" value={data.notes} />
          </div>
        </Section>

        <div style={{ height: 0.5, background: T.hairStrong }} />

        {/* ── Dados da Ativação ──────────────────────────────────────────── */}
        <Section icon="zap" title="Dados da Ativação">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, paddingLeft: 32 }}>
            {/* Provider brand tag */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 10.5, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>
                Provedora
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  width: 22, height: 22, borderRadius: 6,
                  background: prov.c, color: "#fff",
                  fontSize: 10, fontWeight: 700,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {prov.l}
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 500, color: T.ink }}>{data.provider || "—"}</span>
              </span>
            </div>
            <Field label="Plano" value={data.plan} wide />
            <Field label="Valor mensal"        value={valueStr}               mono accent />
            <Field label="Contrato"            value={data.contract}          mono />
            <Field label="Dia do vencimento"   value={data.day ? String(data.day).padStart(2, "0") : undefined} mono />
            <Field label="Período"             value={data.period} />
            <Field label="Data da ativação"    value={activationDateFmt}      mono />
            <Field label="Data da instalação"  value={installationDateFmt}    mono />
            <Field label="Pagamento"           value={data.paidAt ? fmtDateTime(data.paidAt) : undefined} mono />
          </div>
        </Section>

        <div style={{ height: 0.5, background: T.hairStrong }} />

        {/* ── Comentários (somente ativações; negativas avulsas não têm) ──── */}
        {!isNegativeRecord && data._id && (
          <>
            <Section icon="message" title={`Comentários${data.comments?.length ? ` (${data.comments.length})` : ""}`}>
              <div style={{ paddingLeft: 32 }}>
                <ActivationComments activation={data} onUpdated={onCommentsUpdated} />
              </div>
            </Section>
            <div style={{ height: 0.5, background: T.hairStrong }} />
          </>
        )}

        {/* ── Histórico / Timeline ───────────────────────────────────────── */}
        <Section icon="clock" title="Histórico">
          <div style={{ paddingLeft: 32 }}>
            <div style={{ position: "relative", paddingLeft: 22 }}>
              {/* Vertical line */}
              <div style={{
                position: "absolute", left: 7, top: 8, bottom: 8,
                width: 1.5, background: T.hairStrong,
              }}/>
              {events.map((ev, i) => (
                <div key={i} style={{ position: "relative", paddingBottom: i < events.length - 1 ? 16 : 0 }}>
                  {/* Dot */}
                  <div style={{
                    position: "absolute", left: -22, top: 3,
                    width: 16, height: 16, borderRadius: "50%",
                    background: T.surface,
                    border: `2px solid ${ev.color}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: ev.color }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: T.ink, fontFamily: T.font }}>{ev.title}</span>
                    <span style={{ fontSize: 11.5, color: T.ink3, fontVariantNumeric: "tabular-nums", fontFamily: T.font, flexShrink: 0 }}>{ev.when}</span>
                  </div>
                  <div style={{ fontSize: 12, color: T.ink2, marginTop: 2, fontFamily: T.font }}>
                    por{" "}
                    <span style={{ color: T.accent }}>{ev.who}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

      </div>
    </div>
  );
}
